import React, { useMemo, useRef, useCallback, useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import ThreadComposer from "./ThreadComposer";
import MediaUploadSection from "./MediaUpload/MediaUploadSection";
import ScheduleSection from "./ScheduleSection";
import CollectionSelector from "./CollectionSelector";
import { ButtonBasic } from "../button/ButtonBasic";
import { isTextOverLimit } from "../../utils/textColorUtils";



const FullScreenEditor = ({
  threads,
  onThreadsChange,
  mediaByThread,
  onThreadMediaChange,
  loading,
  loadingStep = "",
  isScheduled,
  setIsScheduled,
  scheduleDateTime,
  setScheduleDateTime,
  collectionConfig,
  setCollectionConfig,
  myCollections,
  handlePost,
  handleThreadsAPIPost,
  isPostDisabled,
  hasOverLimitText,
  MAX_TEXT_LENGTH,
  // 임시저장 관련 props 추가
  onSaveDraft,
  hasNoContent = false,
  // 수정 모드 관련 props 추가
  isEditMode = false,
  onDelete,
  onUpdateDraft,
  // 임시저장 더티 상태 및 모드 정보
  isDraftDirty = false,
  isEditDraft = false,
}) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    
    // 스크롤 컨테이너 ref
    const scrollContainerRef = useRef(null);
    // 스크롤 위치 저장용 ref
    const scrollPositionRef = useRef(0);

  // 스크롤 초기화 방지를 위한 안정적인 previewThreads 관리
  const [stablePreviewThreads, setStablePreviewThreads] = useState(threads);

  const formatThreadMedia = useCallback((sourceMedia = []) => {
    return (Array.isArray(sourceMedia) ? sourceMedia : [])
      .map((item) => {
        if (!item) return null;
        const type = (item.type || item.fileType || '').toLowerCase();
        const isVideo = type.startsWith('video/');
        const url = item.preview || item.url || '';
        if (!url) return null;
        return {
          id: item.id || item.storagePath || item.url || `media-${Math.random()}`,
          url,
          type: isVideo ? 'video' : 'image'
        };
      })
      .filter(Boolean);
  }, []);

  const resolveMediaForThread = useCallback((threadId) => {
    if (mediaByThread instanceof Map) {
      return mediaByThread.get(threadId) || [];
    }
    if (mediaByThread && typeof mediaByThread === 'object') {
      return mediaByThread[threadId] || [];
    }
    return [];
  }, [mediaByThread]);

  // 디바운싱을 통해 previewThreads 업데이트 빈도 줄이기
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const mainThread = threads.find((t) => t.isMain);
      if (!mainThread || !isTextOverLimit(mainThread.text, MAX_TEXT_LENGTH)) {
        setStablePreviewThreads(threads);
        return;
      }

      // 메인 스레드가 500자 초과인 경우 자동 분할
      const text = mainThread.text;
      const chunks = [];
      let currentChunk = "";
      const words = text.split(" ");

      for (const word of words) {
        const testChunk = currentChunk ? `${currentChunk} ${word}` : word;

        if (!isTextOverLimit(testChunk, MAX_TEXT_LENGTH)) {
          currentChunk = testChunk;
        } else {
          if (currentChunk) {
            chunks.push(currentChunk);
            currentChunk = word;
          } else {
            // 단어 자체가 500자를 초과하는 경우
            chunks.push(word.substring(0, MAX_TEXT_LENGTH));
            currentChunk = word.substring(MAX_TEXT_LENGTH);
          }
        }
      }

      if (currentChunk) {
        chunks.push(currentChunk);
      }

      // 분할된 청크로 새로운 threads 배열 생성
      const newThreads = [];
      chunks.forEach((chunk, index) => {
        newThreads.push({
          id: index + 1,
          text: chunk,
          isMain: index === 0,
        });
      });

      // 기존 댓글들 추가
      const existingReplies = threads.filter((t) => !t.isMain);
      existingReplies.forEach((reply, index) => {
        newThreads.push({
          ...reply,
          id: chunks.length + index + 1,
        });
      });

      setStablePreviewThreads(newThreads);
    }, 300); // 300ms 디바운싱

    return () => clearTimeout(timeoutId);
  }, [threads, MAX_TEXT_LENGTH]);

  // 텍스트 영역의 높이를 자동 조절하는 함수 (스크롤 위치 보존)
  const adjustTextareaHeight = useCallback((element) => {
    if (element && scrollContainerRef.current) {
      // 현재 스크롤 위치 저장
      const currentScrollTop = scrollContainerRef.current.scrollTop;
      
      // textarea 높이 조절
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
      
      // 스크롤 위치 복원
      scrollContainerRef.current.scrollTop = currentScrollTop;
    }
  }, []);

  // 모든 텍스트 영역의 높이를 조절하는 useEffect (스크롤 위치 보존)
  useEffect(() => {
    if (scrollContainerRef.current) {
      // 현재 스크롤 위치 저장
      const currentScrollTop = scrollContainerRef.current.scrollTop;
      
      const textareas = document.querySelectorAll('textarea[data-thread-id]');
      textareas.forEach(textarea => {
        // 각 textarea 높이 조절 (adjustTextareaHeight 내부에서도 스크롤 보존)
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      });
      
      // 스크롤 위치 복원
      scrollContainerRef.current.scrollTop = currentScrollTop;
    }
  }, [threads]);

  // 메인 스레드의 첫 문장을 제목으로 추출 - 직접 계산
  const title = useMemo(() => {
    const mainThread = threads.find((t) => t.isMain);
    if (!mainThread || !mainThread.text.trim()) {
      return "제목 없음";
    }

    const text = mainThread.text.trim();
    const sentences = text.split(/[.!?\n]/);
    const firstSentence = sentences[0].trim();

    if (firstSentence.length > 50) {
      return firstSentence.substring(0, 50) + "...";
    }

    return firstSentence || "제목 없음";
  }, [threads]);

  // 스레드 텍스트 변경 (스크롤 위치 보존)
  const handleTextChange = useCallback((threadId, text) => {
    // 스크롤 위치 저장
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
    
    const updatedThreads = threads.map((thread) =>
      thread.id === threadId ? { ...thread, text } : thread
    );
    onThreadsChange(updatedThreads);
    
    // 다음 렌더링 후 스크롤 위치 복원
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollPositionRef.current;
      }
    });
  }, [threads, onThreadsChange]);

  // 새 스레드 추가
  const addThread = useCallback(() => {
    const newThread = {
      id: Date.now(),
      text: "",
      isMain: false,
    };
    onThreadsChange([...threads, newThread]);
  }, [threads, onThreadsChange]);

  // 스레드 삭제
  const removeThread = useCallback((threadId) => {
    if (threads.length > 1) {
      const confirmed = window.confirm("이 문단을 삭제하시겠습니까?");
      if (confirmed) {
    onThreadsChange(threads.filter((thread) => thread.id !== threadId));
      }
    }
  }, [threads, onThreadsChange]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 80px)", // 헤더 높이 제외하여 fullpage 구현
        width: "100%",
        display: "flex",
        overflow: "hidden", // 페이지 레벨 스크롤 방지
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* 좌측 메인 에디터 영역 - 스크롤 컨테이너 고정 */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          height: "100%",
          overflow: "auto", // 스크롤 컨테이너 (재렌더링 안됨)
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(6, 4),
        }}
      >
        {/* 제목 영역 */}
        <Typography
          variant="h3"
          sx={{
            marginBottom: theme.spacing(4),
            color: theme.palette.text.primary,
            fontWeight: 700,
            lineHeight: 1.4,
            fontFamily: '"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif',
            opacity: 0.8,
            wordBreak: "break-word",
          }}
        >
          {title}
        </Typography>

        {/* 스레드 입력 영역들 - 내용만 업데이트됨 */}
        {threads.map((thread, index) => {
          const threadMediaFiles = resolveMediaForThread(thread.id);
          const maxFiles = thread.isMain ? 8 : 5;
          const previewMedia = formatThreadMedia(threadMediaFiles);

          return (
          <Box
            key={thread.id}
            sx={{
              marginBottom: index < threads.length - 1 ? theme.spacing(4) : 0,
            }}
          >
            {/* 스레드별 텍스트 입력 */}
            <Box
              component="textarea"
              value={thread.text}
              onChange={(e) => handleTextChange(thread.id, e.target.value)}
              placeholder={
                thread.isMain
                  ? "내용을 작성해보세요..."
                  : "문단을 이어서 작성해보세요..."
              }
              data-thread-id={thread.id}
              ref={(element) => {
                if (element) {
                  // 초기 렌더링 시와 텍스트 변경 시 높이 조절
                  adjustTextareaHeight(element);
                }
              }}
              sx={{
                width: "100%",
                border: "none",
                outline: "none",
                backgroundColor: "transparent",
                fontSize: "22px",
                fontWeight: 500,
                lineHeight: 1.6,
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.primary,
                resize: "none",
                overflow: "hidden",
                minHeight: thread.isMain ? "120px" : "80px",
                padding: 0,
                margin: 0,
                "&::placeholder": {
                  color: theme.palette.text.secondary,
                  opacity: 0.7,
                },
                "&:focus": {
                  outline: "none",
                },
                caretColor: theme.palette.primary.main,
              }}
              onInput={(e) => {
                // 스크롤 위치 저장
                const currentScrollTop = scrollContainerRef.current?.scrollTop || 0;
                
                // 자동 높이 조절
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
                
                // 스크롤 위치 복원
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollTop = currentScrollTop;
                }
              }}
            />

            {/* 삭제 버튼 (메인 스레드가 아닌 경우만) */}
            {!thread.isMain && (
              <Box sx={{ textAlign: "right", marginTop: theme.spacing(1) }}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeThread(thread.id)}
                  sx={{
                    fontSize: "12px",
                    padding: theme.spacing(0.5, 1),
                    minWidth: "auto",
                  }}
                >
                  삭제
                </Button>
              </Box>
            )}

            {/* 문단 구분선 (마지막 스레드가 아닌 경우) */}
            {previewMedia.length > 0 && (
              <ThreadMediaPreview mediaList={previewMedia} />
            )}

            <MediaUploadSection
              mediaFiles={threadMediaFiles}
              onMediaFilesChange={(files) => onThreadMediaChange && onThreadMediaChange(thread.id, files)}
              maxFiles={maxFiles}
              disabled={loading}
              variant="inline"
              showConnectionLine={false}
            />

            {index < threads.length - 1 && (
              <Box
                sx={{
                  height: "1px",
                  backgroundColor: theme.palette.divider,
                  opacity: 0.3,
                  marginTop: theme.spacing(3),
                  marginBottom: theme.spacing(1),
                }}
              />
            )}
          </Box>
        );
        })}

        {/* 문단 추가 버튼 */}
        <Box sx={{ marginTop: theme.spacing(4) }}>
          <Button
            variant="outlined"
            onClick={addThread}
            sx={{
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              backgroundColor: "transparent",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main + "0a",
              },
            }}
          >
            + 문단 추가
          </Button>
        </Box>
      </Box>

      {/* 우측 프리뷰 + 예약게시 영역 - 고정 너비로 패딩 포함 */}
      <Box
        sx={{
          width: "480px", // 고정 너비 (Preview 384px + 좌우 패딩 48px * 2)
          minWidth: "480px", // 최소 너비 보장
          maxWidth: "480px", // 최대 너비 제한
          height: "100%",
          backgroundColor: theme.palette.grey[25],
          borderLeft: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "column",
          overflow: "auto", // 전체 사이드바 스크롤
        }}
      >
        {/* 모바일 프리뷰 영역 */}
        <Box
          sx={{
            height: "auto", // 자연스러운 높이
            padding: theme.spacing(3, 4, 2, 4),
            display: "flex",
            justifyContent: "center", // 중앙 정렬
          }}
        >
          {/* Preview 모드와 동일한 384px 컨테이너 */}
          <Box
            sx={{
              width: "384px", // Preview 모드와 동일한 모바일 프레임 사이즈
              position: "relative",
            }}
          >
            {/* 로딩 상태 표시 */}
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  padding: 3,
                  borderRadius: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  minWidth: "200px",
                  // 은은한 배경 그라데이션 애니메이션
                  background: `
                    linear-gradient(45deg, 
                      rgba(255, 255, 255, 0.95), 
                      rgba(248, 250, 252, 0.95), 
                      rgba(241, 245, 249, 0.95)
                    )
                  `,
                  animation: "subtleGradient 3s ease-in-out infinite",
                  "@keyframes subtleGradient": {
                    "0%, 100%": {
                      background: `
                        linear-gradient(45deg, 
                          rgba(255, 255, 255, 0.95), 
                          rgba(248, 250, 252, 0.95), 
                          rgba(241, 245, 249, 0.95)
                        )
                      `,
                    },
                    "50%": {
                      background: `
                        linear-gradient(45deg, 
                          rgba(248, 250, 252, 0.95), 
                          rgba(241, 245, 249, 0.95), 
                          rgba(235, 241, 247, 0.95)
                        )
                      `,
                    },
                  },
                }}
              >
                {/* 로딩 스피너 */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    border: "3px solid #f3f4f6",
                    borderTop: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
                
                {/* 로딩 텍스트 */}
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  게시 중...
                </Typography>
              </Box>
            )}
            
            <ThreadComposer
              threads={stablePreviewThreads}
              onThreadsChange={() => {}}
              mediaByThread={mediaByThread}
              onThreadMediaChange={() => {}}
              loading={loading}
              maxThreads={10}
              readOnly={true}
              showMediaUpload={false}
              mediaUploadVariant="inline"
              showConnectionLine={false}
            />
          </Box>
        </Box>

        {/* 예약게시 세팅 영역 */}
        <Box
          sx={{
            height: "auto", // 자연스러운 높이
            backgroundColor: theme.palette.grey[25],
            padding: theme.spacing(3, 4, 4, 4),
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* 예약 시간 설정 */}
          <Box sx={{ marginBottom: theme.spacing(3) }}>
            <ScheduleSection
              isScheduled={isScheduled}
              setIsScheduled={setIsScheduled}
              scheduleDateTime={scheduleDateTime}
              setScheduleDateTime={setScheduleDateTime}
              loading={loading}
              hideToggle={false}
            />
          </Box>

          {/* 컬렉션 선택: 항상 표시하되 예약이 아닐 때 비활성화 */}
          <Box sx={{ marginBottom: theme.spacing(3) }}>
            <CollectionSelector
              value={collectionConfig}
              onChange={setCollectionConfig}
              collections={myCollections}
              loading={loading}
              disabled={loading || !isScheduled}
            />
          </Box>

          {/* 게시 버튼 */}
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <ButtonBasic
              onClick={isScheduled ? handlePost : handleThreadsAPIPost}
              loading={loading}
              disabled={isPostDisabled}
              size="xl"
              widthType="full"
              actionType="primary"
              radius="full"
            >
              {loading && loadingStep ? loadingStep : (isScheduled ? "예약 게시하기" : "게시하기")}
            </ButtonBasic>
          </Box>
          
          {/* 임시저장 버튼 - scheduled 수정 모드에서는 숨김 */}
          {(!isEditMode || !location.pathname.includes('/scheduled/')) && (
            <Box sx={{ marginBottom: theme.spacing(2) }}>
              <ButtonBasic
                onClick={() => {
                  if (isEditMode) {
                    if (isEditDraft && !isDraftDirty) return; // 변경 없음이면 무시
                    onUpdateDraft?.();
                  } else {
                    onSaveDraft?.();
                  }
                }}
                variant="outlined"
                disabled={
                  hasNoContent ||
                  loading ||
                  (isEditMode && isEditDraft && !isDraftDirty)
                }
                size="xl"
                widthType="full"
                radius="full"
              >
                임시저장
              </ButtonBasic>
            </Box>
          )}

          {/* 하단 툴바 - 수정 모드에서만 표시 */}
          {isEditMode && onDelete && (
            <Box sx={{ 
              display: 'flex',
              gap: 1,
              marginBottom: theme.spacing(2),
              paddingTop: theme.spacing(2),
              borderTop: `1px solid ${theme.palette.divider}`
            }}>
              <ButtonBasic
                onClick={onDelete}
                variant="outlined"
                color="error"
                disabled={loading}
                size="lg"
                widthType="auto"
                radius="full"
                sx={{ flex: 1 }}
              >
                삭제하기
              </ButtonBasic>
              <ButtonBasic
                onClick={() => navigate('/post-thread')}
                variant="outlined"
                disabled={loading}
                size="lg"
                widthType="auto"
                radius="full"
                sx={{ flex: 1 }}
              >
                새글 작성
              </ButtonBasic>
            </Box>
          )}

          {/* 오류 메시지 */}
          {hasOverLimitText && (
            <Box
              sx={{
                fontSize: "14px",
                color: theme.palette.error.main,
                textAlign: "center",
                padding: theme.spacing(1),
                backgroundColor: theme.palette.error.light + "20",
                borderRadius: theme.spacing(0.5),
                marginBottom: theme.spacing(2),
              }}
            >
              일부 텍스트가 {MAX_TEXT_LENGTH}자를 초과했습니다. 내용을
              줄여주세요.
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ThreadMediaPreview = ({ mediaList }) => {
  const theme = useTheme();
  if (!mediaList || mediaList.length === 0) return null;

  const ITEM_HEIGHT = 220;

  return (
    <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          paddingY: 1,
          paddingX: 0.5,
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[200]}`
        }}
      >
        {mediaList.map((media, idx) => (
          <Box
            key={media.id || idx}
            sx={{
              flex: '0 0 auto',
              borderRadius: theme.spacing(1.5),
              overflow: 'hidden',
              border: `1px solid ${theme.palette.grey[200]}`,
              backgroundColor: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: ITEM_HEIGHT
            }}
          >
            {media.type === 'video' ? (
              <Box
                component="video"
                src={media.url}
                controls
                muted
                playsInline
                sx={{ height: '100%', width: 'auto', display: 'block' }}
              />
            ) : (
              <Box
                component="img"
                src={media.url}
                alt="media"
                sx={{ height: '100%', width: 'auto', display: 'block' }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FullScreenEditor;
